'use client';

import React, { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import Script from 'next/script';

// Paths where ads should never be displayed or script should not be loaded
export const BLACKLISTED_PATHS = [
  '/about-us',
  '/contact',
  '/privacy-policy',
  '/terms-of-service',
  '/disclaimer',
  '/dmca',
  '/cookie-policy',
  '/seed',
];

/**
 * Script component to load the Google AdSense tags conditionally.
 * Ensures the Google AdSense bundle is NEVER loaded on utility, legal, or seeding routes.
 */
export function AdSenseScript({ publisherId = process.env.NEXT_PUBLIC_ADSENSE_PUB_ID || 'pub-1494570418385979' }) {
  const pathname = usePathname();

  // Guard against missing pathname, placeholder ID, or blacklisted route match
  const isBlacklisted = pathname && BLACKLISTED_PATHS.some(path => 
    pathname === path || pathname.startsWith(path + '/')
  );

  if (isBlacklisted || !publisherId || publisherId.includes('XXXX')) {
    return null;
  }

  return (
    <Script
      async
      src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-${publisherId}`}
      crossOrigin="anonymous"
      strategy="lazyOnload"
    />
  );
}

/**
 * Custom AdUnit component.
 * Safely initializes AdSense and guards against utility pages and layout shifts.
 */
export function AdUnit({ 
  publisherId = process.env.NEXT_PUBLIC_ADSENSE_PUB_ID || 'pub-1494570418385979', 
  slot, 
  format = 'auto', 
  responsive = 'true', 
  style 
}) {
  const pathname = usePathname();
  const adRef = useRef(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const isBlacklisted = pathname && BLACKLISTED_PATHS.some(path => 
    pathname === path || pathname.startsWith(path + '/')
  );

  if (isBlacklisted || !publisherId || publisherId.includes('XXXX')) {
    return null;
  }

  useEffect(() => {
    // Only execute on client-side, if slot is present, and page is not blacklisted
    if (typeof window === 'undefined' || isBlacklisted || !slot) return;

    try {
      // Ensure the global adsbygoogle array exists and push the configuration
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      setHasLoaded(true);
    } catch (err) {
      console.error('AdSense Initialization Error on AdUnit:', err);
    }
  }, [pathname, isBlacklisted, slot]);

  if (isBlacklisted || !slot) {
    return null;
  }

  return (
    <div 
      className="my-6 mx-auto flex justify-center items-center overflow-hidden w-full max-w-full min-h-[90px] bg-secondary/5 rounded-xl border border-border/30 p-2" 
      ref={adRef}
    >
      <ins
        className="adsbygoogle"
        style={style || { display: 'block' }}
        data-ad-client={`ca-${publisherId}`}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive}
      />
    </div>
  );
}
